/**
 * Research Report DOCX Generator
 *
 * Generates professionally formatted DOCX documents matching the research PDF format
 * with cover page, prepared by information, and content pages.
 *
 * DOCX Specifications:
 * - A4 page size (210mm x 297mm)
 * - Font: Times New Roman (serif) for legal document standard
 * - Cover page with title, attribution, date, copyright
 * - Content pages with heading hierarchy (H2/H3/H4) and body text
 * - Page numbers and footers
 */

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Footer,
  PageNumber,
  AlignmentType,
  convertInchesToTwip,
} from 'docx';
import {
  normalizeContent,
  extractAndPreserveReferences,
  convertReferencesToPlaintext,
} from './pdfTextUtils';

// ============================================================================
// Types (matching reportPdf.ts)
// ============================================================================

/**
 * Translation overrides for creating translated DOCX documents.
 */
export interface DocxTranslationOverrides {
  /** Translated document title */
  title?: string;
  /** Translated date string (e.g., "15 de enero de 2024") */
  date?: string;
  /** Translated "Prepared by" line (e.g., "Preparado por: Fernando Hidalgo") */
  preparedBy?: string;
  /** Firm name (typically kept as-is) */
  firmName?: string;
  /** Translated full copyright notice */
  copyrightNotice?: string;
  /** Translated short copyright for footers */
  copyrightShort?: string;
}

export interface BuildResearchDocxParams {
  title: string;
  prompt: string;
  content: string;
  createdAt?: Date;
  /** Optional translation overrides for creating translated DOCX files */
  translations?: DocxTranslationOverrides;
}

// ============================================================================
// Constants
// ============================================================================

// MT-QA-BUG-001: FIRM_NAME is a last-resort fallback only. Callers that
// know their tenant (Deep Research, Maestro, LDC) must thread the active
// tenant's firm name via `translations.firmName`.
const FIRM_NAME = 'The Law Offices of Fernando Hidalgo, Inc.';

// MT-QA-BUG-001 follow-up (2026-05-08 §132a regression): DOCX was shipping
// with the placeholder "Legal AI Assistant" on the title page. Replace
// with the product brand label so every tenant's DOCX reads
// "Generated by: Prest0n". The constant is NOT tenant-specific.
const PRODUCT_NAME = 'Prest0n';

// A4 page dimensions in twips (1 inch = 1440 twips)
const A4_WIDTH_TWIP = convertInchesToTwip(8.27);   // 210mm
const A4_HEIGHT_TWIP = convertInchesToTwip(11.69);  // 297mm

// ============================================================================
// DOCX Builder
// ============================================================================

export async function buildResearchDocx(params: BuildResearchDocxParams): Promise<File> {
  const createdAt = params.createdAt || new Date();
  const year = createdAt.getFullYear();
  const translations = params.translations || {};

  // Use translated values if provided, otherwise use defaults
  const firmName = translations.firmName || FIRM_NAME;
  const dateStr =
    translations.date ||
    createdAt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const generatedByText = translations.preparedBy || `Generated by: ${PRODUCT_NAME}`;
  const facilitatedByText = `Facilitated by: ${firmName}`;
  const copyrightNotice =
    translations.copyrightNotice ||
    `(c) ${year} ${firmName}. Generated by ${PRODUCT_NAME}. Facilitated by ${firmName}. All rights reserved.`;
  const copyrightShort = translations.copyrightShort || `© ${year} ${firmName}`;

  const displayTitle = translations.title || params.title || 'Legal Research Report';

  // Split content into body and references to preserve URLs in references section
  // useUnicodeFonts=true since DOCX handles Unicode natively
  const { body, references } = extractAndPreserveReferences(params.content);
  const normalizedBody = normalizeContent(body, true);
  let normalizedReferences = '';
  if (references) {
    const refsWithVisibleUrls = convertReferencesToPlaintext(references);
    normalizedReferences = normalizeContent(refsWithVisibleUrls, true);
  }
  const normalizedContent = normalizedBody + (normalizedReferences ? '\n\n' + normalizedReferences : '');

  // A4 page margins per spec: 54pt left/right (0.75"), 39pt top (0.54"), 36pt bottom (0.5")
  const pageMargins = {
    top: convertInchesToTwip(0.54),    // 39pt top margin
    bottom: convertInchesToTwip(0.5),  // 36pt footer area
    left: convertInchesToTwip(0.75),   // 54pt left margin
    right: convertInchesToTwip(0.75),  // 54pt right margin
  };

  // Build cover page content
  const coverPageChildren: Paragraph[] = [
    // Spacing to position title approximately 72pt from top of page
    // With 39pt top margin, need ~33pt (660 twips) of additional space
    new Paragraph({ spacing: { before: 660 } }),

    // Document title (centered, bold, 22pt) — Times-Bold 22pt
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 720 }, // ~36pt spacer below title block
      children: [
        new TextRun({
          text: displayTitle,
          bold: true,
          size: 44, // 22pt in half-points
          font: 'Times New Roman',
        }),
      ],
    }),

    // Generated by (centered) — Times-Roman 12pt
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 120 },
      children: [
        new TextRun({
          text: generatedByText,
          size: 24, // 12pt
          font: 'Times New Roman',
        }),
      ],
    }),

    /* Facilitated by removed from cover per request
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 120 },
      children: [
        new TextRun({
          text: facilitatedByText,
          size: 24, // 12pt
          font: 'Times New Roman',
        }),
      ],
    }),
    */

    // Date (centered, dark gray #4D544D) — Times-Roman 11pt
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 480 }, // ~24pt spacer below attribution block
      children: [
        new TextRun({
          text: dateStr,
          size: 22, // 11pt
          font: 'Times New Roman',
          color: '4D544D',
        }),
      ],
    }),

    /* Copyright removed from mid-cover page to rely on footer
    // Copyright notice (centered, italic, 9pt, medium gray #666666)
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({
          text: copyrightNotice,
          italics: true,
          size: 18, // 9pt
          font: 'Times New Roman',
          color: '666666',
        }),
      ],
    }),
    */
  ];

  // Build content paragraphs with heading hierarchy
  const contentChildren: Paragraph[] = [];

  // Document title block at top of content section — ALL CAPS, Times-Bold 14pt
  contentChildren.push(
    new Paragraph({
      spacing: { after: 260 }, // ~13pt space after document title block
      children: [
        new TextRun({
          text: displayTitle.toUpperCase(),
          bold: true,
          size: 28, // 14pt in half-points
          font: 'Times New Roman',
        }),
      ],
    })
  );

  // Helper: detect heading level from a line of text.
  // Handles markdown headings (## Heading), Roman numeral sections (II. Heading),
  // and combined formats (## II. Heading) that Perplexity deep research may produce.
  const detectHeading = (line: string): { level: number; text: string } | null => {
    // 1. Markdown headings: # through ######
    const mdMatch = line.match(/^(#{1,6})\s+(.+)/);
    if (mdMatch) {
      return { level: mdMatch[1].length, text: mdMatch[2] };
    }
    // 2. Roman numeral section headers (I. through XX.) — treat as H2
    const romanMatch = line.match(/^([IVX]{1,5})\.\s+(.+)/);
    if (romanMatch) {
      return { level: 2, text: `${romanMatch[1]}. ${romanMatch[2]}` };
    }
    return null;
  };

  // Helper: check if text is a references/bibliography section name
  const isReferencesHeader = (text: string): boolean =>
    /^(?:[IVX]{1,5}\.\s+)?(references|bibliography|sources?\s*cited|works\s*cited|complete\s*source\s*citations)$/i.test(text);

  // Parse and render paragraphs with heading hierarchy detection
  let inReferences = false;
  const paragraphs = normalizedContent.split('\n');

  for (const para of paragraphs) {
    const trimmed = para.trim();
    if (!trimmed) continue;

    const heading = detectHeading(trimmed);

    if (heading) {
      // Check if this is the References section header
      if (isReferencesHeader(heading.text)) {
        inReferences = true;
      }

      if (heading.level <= 2) {
        // H2 — Section header: Times-Bold 13pt, no indent
        contentChildren.push(
          new Paragraph({
            spacing: { before: 200, after: 180 }, // 10pt before, 9pt after
            children: [
              new TextRun({
                text: heading.text,
                bold: true,
                size: 26, // 13pt in half-points
                font: 'Times New Roman',
              }),
            ],
          })
        );
      } else if (heading.level === 3) {
        // H3 — Sub-section header: Times-Bold 12pt, 8pt left indent
        contentChildren.push(
          new Paragraph({
            indent: { left: 160 }, // 8pt in twips (8 * 20)
            spacing: { before: 160, after: 140 }, // 8pt before, 7pt after
            children: [
              new TextRun({
                text: heading.text,
                bold: true,
                size: 24, // 12pt in half-points
                font: 'Times New Roman',
              }),
            ],
          })
        );
      } else {
        // H4+ — Sub-sub-section header: Times-Bold 11pt, 8pt left indent
        contentChildren.push(
          new Paragraph({
            indent: { left: 160 }, // 8pt in twips
            spacing: { before: 160, after: 120 }, // 8pt before, 6pt after
            children: [
              new TextRun({
                text: heading.text,
                bold: true,
                size: 22, // 11pt in half-points
                font: 'Times New Roman',
              }),
            ],
          })
        );
      }
    } else {
      // Check for unheaded references section marker (plain "References" line)
      if (isReferencesHeader(trimmed)) {
        inReferences = true;
        contentChildren.push(
          new Paragraph({
            spacing: { before: 200, after: 180 },
            children: [
              new TextRun({
                text: trimmed,
                bold: true,
                size: 26, // 13pt
                font: 'Times New Roman',
              }),
            ],
          })
        );
        continue;
      }

      // Body text — Times-Roman 11pt
      // Strip remaining structural markers (bullets, numbers)
      const text = trimmed
        .replace(/^[-*]\s+/, '')
        .replace(/^\d+\.\s+/, '');
      if (text) {
        // Reference entries use 6pt (120 twips) spacing; body text uses 8pt (160 twips)
        const afterSpacing = inReferences ? 120 : 160;
        contentChildren.push(
          new Paragraph({
            spacing: { after: afterSpacing },
            children: [
              new TextRun({
                text,
                size: 22, // 11pt
                font: 'Times New Roman',
              }),
            ],
          })
        );
      }
    }
  }

  // Create the document with sections
  const doc = new Document({
    title: displayTitle,
    creator: firmName,
    description: params.prompt || 'Legal Research Report',
    sections: [
      // Cover page section (now includes footer to show on every page)
      {
        properties: {
          page: {
            size: {
              width: A4_WIDTH_TWIP,
              height: A4_HEIGHT_TWIP,
            },
            margin: pageMargins,
          },
        },
        footers: {
          default: new Footer({
            children: [
              // Page number
              new Paragraph({
                alignment: AlignmentType.CENTER,
                spacing: { before: 200 },
                children: [
                  new TextRun({
                    children: ['Page ', PageNumber.CURRENT],
                    size: 18, // 9pt
                    font: 'Times New Roman',
                    color: '666666',
                  }),
                ],
              }),
              // Copyright
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    text: copyrightShort,
                    italics: true,
                    size: 16, // 8pt
                    font: 'Times New Roman',
                    color: '808080',
                  }),
                ],
              }),
            ],
          }),
        },
        children: coverPageChildren,
      },
      // Content section with footer
      {
        properties: {
          page: {
            size: {
              width: A4_WIDTH_TWIP,
              height: A4_HEIGHT_TWIP,
            },
            margin: pageMargins,
          },
        },
        footers: {
          default: new Footer({
            children: [
              // Page number — Times-Roman 9pt, medium gray (#666666)
              new Paragraph({
                alignment: AlignmentType.CENTER,
                spacing: { before: 200 },
                children: [
                  new TextRun({
                    children: ['Page ', PageNumber.CURRENT],
                    size: 18, // 9pt
                    font: 'Times New Roman',
                    color: '666666',
                  }),
                ],
              }),
              // Copyright — Times-Italic 8pt, gray (#808080)
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    text: copyrightShort,
                    italics: true,
                    size: 16, // 8pt
                    font: 'Times New Roman',
                    color: '808080',
                  }),
                ],
              }),
            ],
          }),
        },
        children: contentChildren,
      },
    ],
  });

  // Generate the DOCX blob
  const buffer = await Packer.toBlob(doc);

  // Generate filename (matching PDF naming convention)
  const slugify = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 50);
  };
  const subjectSlug = slugify(params.prompt || params.title || 'report');
  const dateSlug = createdAt.toISOString().slice(0, 10);
  const filename = `${subjectSlug}-${dateSlug}.docx`;

  return new File([buffer], filename, {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    lastModified: createdAt.getTime(),
  });
}
